import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Moment from "moment";

import { DataTable, SearchBar } from "../ui";
import { CUSTOMERS_PAGE_SIZE_OPTIONS } from "../../constants/config";

const formatPhone = (mobile) => {
    if (!mobile) return "";
    return `(${mobile.slice(0, 3)}) ${mobile.slice(3, 6)}-${mobile.slice(6)}`;
};

const CustomersTable = ({
    customers,
    action,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
    loading,
    handleClickOpen,
    refreshCustomersList,
    filters = [],
    search = "",
    onSearchChange,
    onSortChange,
    defaultSort,
    emptyMessage = "No customers to display.",
}) => {
    const columns = useMemo(
        () => [
            {
                id: "firstName",
                label: "Customer",
                sortable: true,
                serverSortField: "firstName",
                cell: (row) => (
                    <div>
                        <p className="leading-tight text-ink-900">
                            {row.firstName} {row.lastName}
                        </p>
                        {row.contractor === "Yes" && (
                            <p className="text-xs leading-none text-brand-600">Contractor</p>
                        )}
                    </div>
                ),
            },
            {
                id: "mobile",
                label: "Mobile",
                sortable: true,
                serverSortField: "mobile",
                cell: (row) => formatPhone(row.mobile),
            },
            { id: "city", label: "City", sortable: true, serverSortField: "city" },
            {
                id: "totalSpent",
                label: "Total Spent",
                sortable: true,
                serverSortField: "totalSpent",
                align: "right",
                hideBelow: "lg",
                cellClassName: "tabular-nums",
                cell: (row) => {
                    const v = Number(row.totalSpent || 0);
                    return v ? `$${v.toFixed(2)}` : null;
                },
            },
            {
                id: "balance",
                label: "Balance",
                sortable: true,
                serverSortField: "balance",
                align: "right",
                cell: (row) => {
                    const v = Number(row.balance || 0);
                    return (
                        <span
                            className={`tabular-nums ${
                                v > 0 ? "text-danger-600" : "text-ink-700"
                            }`}
                        >
                            {v === 0 ? "—" : `$${v.toFixed(2)}`}
                        </span>
                    );
                },
            },
            {
                id: "latestActivity",
                label: "Last Activity",
                sortable: true,
                serverSortField: "latestActivity",
                hideBelow: "lg",
                cellClassName: "text-ink-500",
                cell: (row) =>
                    row.latestActivity ? Moment(row.latestActivity).format("MMM D, YYYY") : null,
            },
        ],
        [],
    );

    const bulkActions = useMemo(
        () => [
            {
                label: "Delete",
                variant: "danger",
                onClick: async (selectedIds) => {
                    await Promise.all(selectedIds.map((id) => action.deleteCustomer(id)));
                    if (refreshCustomersList) refreshCustomersList();
                },
                successMessage: "Record(s) successfully deleted",
                errorMessage: "An error occurred while deleting. Please contact support.",
            },
        ],
        [action, refreshCustomersList],
    );

    return (
        <DataTable
            rows={customers}
            columns={columns}
            onSortChange={onSortChange}
            topContent={
                <SearchBar
                    id="customers-search-table"
                    value={search}
                    onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                    placeholder="Search name, phone, city, or company"
                />
            }
            selectable
            defaultSort={defaultSort || { id: "firstName", direction: "asc" }}
            onRowClick={(row) => handleClickOpen(row._id)}
            bulkActions={bulkActions}
            filters={filters}
            loading={loading}
            emptyMessage={emptyMessage}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            rowsPerPageOptions={CUSTOMERS_PAGE_SIZE_OPTIONS}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
        />
    );
};

CustomersTable.propTypes = {
    customers: PropTypes.array.isRequired,
    action: PropTypes.object.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    handleClickOpen: PropTypes.func.isRequired,
    refreshCustomersList: PropTypes.func,
    filters: PropTypes.array,
    search: PropTypes.string,
    onSearchChange: PropTypes.func,
    emptyMessage: PropTypes.string,
};

export default CustomersTable;
